// Таблица турнира (по StandingsTable iOS). Группы — по stage в порядке появления (сервер сортирует сквозь группы).
// Зона — серверная ПОДПИСЬ (не цвет): цвет — палитра[zone_index] (номер зоны в этапе по всей таблице, HQ-1782), без поля
// (старый сервер) — по порядку появления зон; полоса 4px слева + легенда.
// Колонки по макету HQ-194: compact (превью) — «# Team PL PT»; full — «# Team PL W L NRR PT». Имя команды полное в обоих (HQ-1063, не код «RCB»).
// Строка по Figma 1025:139913 (HQ-1909): 32, линия 0.5, «#» и числа 13/400 --content-secondary, значок 18 кругом, имя 13/400
// одной строкой с «…» (крупный шрифт на узком экране — до 3 строк, HQ-1955), PT 13/600; шапка колонок — так же. Метка зоны — 4 px слева (Tag); highlight — строка «своей» команды
// (заливка #34C759@.16 и метка: зона строки, без зоны — --positive).
// match — мини-таблица матча до начала (Figma 815:25252, HQ-1166): «# Team G W PT», команды матча — зелёной полосой 4px
// слева вместо заливки (зона строки, если есть, важнее — та же полоса).
// Шапки PL/G/W/L/PT — словами языка (t, HQ-1567), NRR — латиницей; шапка одной строкой, место уступает колонка команды.
// Крупный шрифт на узком экране (контейнер standings в CSS, HQ-1955, слово штаба 29.10): шапки чисел — латинские сокращения
// макета PL/G/W/L/PT (Figma 1025:139564) поверх слова языка (CSS по набору колонок data-cols), имя команды — до 3 строк по
// словам; таблица не шире экрана во всех языках.
// Во всех режимах имя команды берёт остаток ширины и сокращается многоточием (teamFill, Team Name «fill» макета).
// Не влезает и так (шрифт ×2) — таблица прокручивается вбок в своей обёртке .scroll, «#» и команда закреплены слева (HQ-1567 v3).
import type { CSSProperties } from 'react';
import type { StandingRow } from '../api/matchTypes';
import { t } from '../i18n/i18n';
import { fixed } from './format';
import { TeamLogo } from './TeamLogo';
import { ZoneLegend } from './ZoneLegend';
import styles from './StandingsTable.module.css';

const ZONE_COLORS = ['var(--positive)', '#FF9F0A', '#0A84FF', 'var(--live)'];

export function groupByStage(rows: StandingRow[]): Array<{ key: string; name: string | null; rows: StandingRow[] }> {
  const groups: Array<{ key: string; name: string | null; rows: StandingRow[] }> = [];
  for (const row of rows) {
    const key = row.stage ? String(row.stage.id) : '';
    let group = groups.find((g) => g.key === key);
    if (!group) groups.push(group = { key, name: row.stage?.name ?? null, rows: [] });
    group.rows.push(row);
  }
  return groups;
}

export function zoneColors(rows: StandingRow[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const row of rows) {
    if (!row.zone || map.has(row.zone)) continue;
    // zone_index — номер зоны по ВСЕМ строкам этапа: в окне (мини-таблица, таблица команды) цвет тот же, что в полной.
    if (typeof row.zone_index === 'number') {
      const color = ZONE_COLORS[row.zone_index];
      if (color) map.set(row.zone, color);
    } else if (map.size < ZONE_COLORS.length) map.set(row.zone, ZONE_COLORS[map.size]!);
  }
  return map;
}

/** Ширина колонки «#» по самому длинному месту: от неё отступ закреплённой колонки команды (--pos-w в CSS). */
export function posDigits(positions: Array<number | string | null | undefined>): CSSProperties {
  return { '--pos-digits': Math.max(1, ...positions.map((p) => String(p ?? '').length)) } as CSSProperties;
}

export function StandingsTable({ rows, compact, mode, highlight, onTeam, showLegend = true }: {
  rows: StandingRow[];
  /** compact — превью на экранах команды и обзора турнира; match — мини-таблица экрана матча; full — вкладка таблицы.
   *  `compact` — прежний флаг, то же, что mode='compact'. */
  compact?: boolean; mode?: 'compact' | 'match' | 'full'; highlight?: number[]; onTeam?: (id: number) => void; showLegend?: boolean;
}) {
  const match = mode === 'match';
  const short = mode ? mode !== 'full' : Boolean(compact);
  const zones = zoneColors(rows);
  return (
    <div>
      <div className={styles.scroll}><table className={styles.table} data-cols={match ? 'match' : short ? 'compact' : 'full'}
        style={posDigits(rows.map((r) => r.position))}>
        <thead>
          <tr>
            <th>#</th><th className={styles.teamCol}>{t('mc_search_teams')}</th>
            {match ? <><th>{t('mc_table_col_games')}</th><th>{t('mc_table_col_won')}</th></> : <th>{t('mc_table_col_played')}</th>}
            {!short && <><th>{t('mc_table_col_won')}</th><th>{t('mc_table_col_lost')}</th><th className={styles.nrr}>NRR</th></>}
            <th className={styles.pts}>{t('mc_table_col_points')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const own = highlight?.includes(r.team.id);
            const zone = (r.zone ? zones.get(r.zone) : undefined) ?? (own ? 'var(--positive)' : undefined);
            return (
              <tr key={`${i}-${r.stage?.id ?? ''}-${r.team.id}`} className={own && !match ? styles.highlight : undefined}
                data-own={match && own ? '' : undefined}
                onClick={() => onTeam?.(r.team.id)}>
                <td className={styles.pos} style={zone ? { boxShadow: `inset 4px 0 0 ${zone}` } : undefined}>{r.position}</td>
                <td className={`${styles.teamCol} ${styles.teamFill}`}>
                  <span className={styles.team}><TeamLogo src={r.team.image} code={r.team.code} size={18} />
                    <span className={styles.code}>{r.team.name}</span></span>
                </td>
                <td>{r.played}</td>
                {match && <td>{r.won}</td>}
                {!short && <><td>{r.won}</td><td>{r.lost}</td><td className={styles.nrr}>{fixed(r.net_run_rate, 3)}</td></>}
                <td className={styles.pts}>{r.points}</td>
              </tr>
            );
          })}
        </tbody>
      </table></div>
      {showLegend && <ZoneLegend zones={zones} />}
    </div>
  );
}
